import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LogOut,
  Calendar,
  Clock,
  Bell,
  CheckCircle,
  Coffee,
  MapPin,
  Plus,
  Trash2,
  BarChart,
  Shield,
  Phone,
  Fingerprint,
  PiggyBank,
} from 'lucide-react';
import { StokvelStaffTab } from '@/components/stokvel/StokvelStaffTab';
import { AttendanceView } from '@/components/shared/AttendanceView';

export default function StaffDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('my-shifts');
  const [myShifts, setMyShifts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unavailability, setUnavailability] = useState([]);
  const [showUnavailability, setShowUnavailability] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(user.branches[0] || '');
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [managerOnDuty, setManagerOnDuty] = useState(null);

  useEffect(() => {
    fetchMyShifts();
    fetchNotifications();
    fetchManagerOnDuty();
    if (activeTab === 'unavailability') {
      fetchUnavailability();
    }
  }, [activeTab, selectedBranch, weekStart]);

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  }

  const fetchMyShifts = async () => {
    try {
      const response = await axios.get(`${API}/shifts/my-shifts`);
      setMyShifts(response.data);
    } catch (error) {
      toast.error('Failed to fetch your shifts');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      // Only display unread notifications - once read they drop off the list.
      setNotifications((response.data || []).filter(n => !n.read));
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchUnavailability = async () => {
    try {
      const weekDates = getWeekDates();
      const response = await axios.get(`${API}/unavailability`, {
        params: {
          date_from: weekDates[0],
          date_to: weekDates[6]
        }
      });
      setUnavailability(response.data);
    } catch (error) {
      console.error('Failed to fetch unavailability');
    }
  };

  const fetchManagerOnDuty = async () => {
    try {
      const response = await axios.get(`${API}/manager-on-duty`, {
        params: { week_start: weekStart }
      });
      setManagerOnDuty(response.data);
    } catch (error) {
      console.error('Failed to fetch manager on duty');
    }
  };

  const handleAddUnavailability = async (data) => {
    try {
      await axios.post(`${API}/unavailability`, {
        ...data,
        user_id: user.id
      });
      toast.success('Unavailability added');
      fetchUnavailability();
      setShowUnavailability(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add unavailability');
    }
  };

  const handleDeleteUnavailability = async (id) => {
    if (!window.confirm('Remove this unavailability period?')) return;
    try {
      await axios.delete(`${API}/unavailability/${id}`);
      toast.success('Unavailability removed');
      fetchUnavailability();
    } catch (error) {
      toast.error('Failed to remove unavailability');
    }
  };

  const handleExportMySchedulePDF = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      week_start: weekStart,
      token: token
    });
    window.open(`${API}/reports/my-schedule/pdf?${params.toString()}`, '_blank');
  };

  const handleRequestShift = async (shiftId) => {
    try {
      await axios.post(`${API}/shift-requests`, { shift_id: shiftId });
      toast.success('Shift requested successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request shift');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      // Once read, immediately remove it from the visible list.
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const getWeekDates = () => {
    const dates = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const changeWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const getISOWeekNumber = (dateStr) => {
    const date = new Date(dateStr);
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const calculateWeeklyHours = () => {
    const weekDates = getWeekDates();
    const weekShifts = myShifts.filter(s => weekDates.includes(s.date));
    
    let totalHours = 0;
    weekShifts.forEach(shift => {
      const start = new Date(`2000-01-01T${shift.start_time}`);
      const end = new Date(`2000-01-01T${shift.end_time}`);
      const hours = (end - start) / (1000 * 60 * 60);
      // Deduct lunch break time
      const lunchHours = (shift.lunch_minutes || 0) / 60;
      totalHours += (hours - lunchHours);
    });
    
    return totalHours.toFixed(1);
  };

  const unreadCount = notifications.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto mobile-padding">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gray-800 p-1.5 sm:p-2 rounded-lg">
                <img 
                  src="https://www.themerchantcoffee.co.za/merchant%20white%20logo_No%20Strap%20-1-.png" 
                  alt="The Merchant Coffee Logo"
                  className="h-6 sm:h-8 w-auto"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base sm:text-xl font-bold text-stone-800">The Merchant Coffee</h1>
                <p className="text-xs text-stone-600">Welcome, {user.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setActiveTab('notifications')}
                data-testid="notifications-button"
                className="relative p-2 hover:bg-stone-100 rounded-lg transition touch-target"
              >
                <Bell className="w-5 h-5 text-stone-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center badge-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>
              <Button onClick={logout} variant="outline" data-testid="logout-button" className="text-xs sm:text-sm touch-target">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Week Display Banner - REMOVED per user request */}

      {/* Manager on Duty Banner */}
      {managerOnDuty && (
        <div className="bg-blue-600">
          <div className="max-w-7xl mx-auto mobile-padding py-2">
            <div className="flex items-center justify-center gap-2 text-white">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Weekend Manager on Duty:</span>
              <span className="text-sm font-bold" data-testid="manager-on-duty-name">{managerOnDuty.manager_username}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto mobile-padding py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
            <TabsList className="bg-white border border-stone-200 p-1 rounded-xl shadow-sm inline-flex min-w-max">
              <TabsTrigger value="my-shifts" data-testid="tab-my-shifts" className="data-[state=active]:bg-gradient-to-r data-[state=active]:bg-gray-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">My Schedule</span>
                <span className="inline sm:hidden">Schedule</span>
              </TabsTrigger>
              <TabsTrigger value="attendance" data-testid="tab-attendance" className="data-[state=active]:bg-gradient-to-r data-[state=active]:bg-gray-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <Fingerprint className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Attendance</span>
                <span className="inline sm:hidden">Att</span>
              </TabsTrigger>
              <TabsTrigger value="unavailability" data-testid="tab-unavailability" className="data-[state=active]:bg-gradient-to-r data-[state=active]:bg-gray-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Unavailability</span>
                <span className="inline sm:hidden">Unavl</span>
              </TabsTrigger>
              {user?.stokvel_enabled && (
                <TabsTrigger value="stokvel" data-testid="tab-stokvel" className="data-[state=active]:bg-gradient-to-r data-[state=active]:bg-gray-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                  <PiggyBank className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">TMC Stokvel</span>
                  <span className="inline sm:hidden">Stokvel</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="notifications" data-testid="tab-notifications" className="data-[state=active]:bg-gradient-to-r data-[state=active]:bg-gray-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <Bell className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Notifications</span>
                <span className="inline sm:hidden">Notif</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* My Shifts Tab */}
          <TabsContent value="my-shifts" className="mt-4 sm:mt-6">
            <div className="bg-white rounded-xl shadow-md border border-stone-200 p-3 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-stone-800">My Schedule</h2>
                  <p className="text-xs sm:text-sm text-stone-600 mt-1">
                    Week of {new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <Button
                    onClick={handleExportMySchedulePDF}
                    data-testid="export-my-schedule-pdf"
                    className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-2 sm:px-3"
                    size="sm"
                  >
                    <BarChart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Export PDF</span>
                    <span className="inline sm:hidden">PDF</span>
                  </Button>
                  <Button onClick={() => changeWeek(-1)} variant="outline" size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
                    <span className="hidden sm:inline">← Previous</span>
                    <span className="inline sm:hidden">←</span>
                  </Button>
                  <Button onClick={() => changeWeek(1)} variant="outline" size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
                    <span className="hidden sm:inline">Next →</span>
                    <span className="inline sm:hidden">→</span>
                  </Button>
                </div>
              </div>

              <div className="staff-schedule-grid grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
                {getWeekDates().map((date, idx) => {
                  const dayShifts = myShifts.filter(s => s.date === date);
                  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                  const fullDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                  return (
                    <div key={date} className="border border-stone-200 rounded-lg p-2 sm:p-3 bg-stone-50 min-h-[100px]">
                      <div className="font-semibold text-xs sm:text-sm text-stone-800 mb-1 sm:mb-2">
                        <span className="sm:hidden">{dayNames[idx]}</span>
                        <span className="hidden sm:inline">{fullDayNames[idx]}</span>
                      </div>
                      <div className="text-[10px] sm:text-xs text-stone-600 mb-2 sm:mb-3">{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                      <div className="space-y-1 sm:space-y-2">
                        {dayShifts.map(shift => (
                          <div
                            key={shift.id}
                            data-testid={`my-shift-${shift.shift_code}`}
                            className="staff-shift-card p-1.5 sm:p-2 rounded text-[10px] sm:text-xs bg-gradient-to-br from-green-100 to-emerald-100 border border-green-300 text-green-800"
                          >
                            <div className="font-semibold">{shift.shift_code}</div>
                            <div className="mt-0.5 sm:mt-1">{shift.start_time}-{shift.end_time}</div>
                            <div className="mt-0.5 sm:mt-1 font-semibold">{shift.assigned_users?.length || 1}/{shift.max_capacity || 1}</div>
                            <div className="mt-0.5 sm:mt-1 flex items-center gap-1">
                              <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                              <span className="truncate">{shift.branch}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {user?.stokvel_enabled && (
            <TabsContent value="stokvel" className="mt-4 sm:mt-6"><StokvelStaffTab /></TabsContent>
          )}

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-6">
            <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-stone-800">Notifications</h2>
                {unreadCount > 0 && (
                  <Button
                    onClick={async () => {
                      await axios.put(`${API}/notifications/read-all`);
                      // Wipe the list immediately - read notifications no longer appear.
                      setNotifications([]);
                    }}
                    data-testid="mark-all-read-button"
                    variant="outline"
                    size="sm"
                  >
                    Mark all as read
                  </Button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-stone-400" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map(notif => (
                    <div
                      key={notif.id}
                      data-testid={`notification-${notif.id}`}
                      onClick={() => markNotificationRead(notif.id)}
                      className="border border-gray-300 bg-gray-50 hover:bg-amber-100 rounded-lg p-4 cursor-pointer transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-stone-800">{notif.title}</div>
                          <div className="text-sm text-stone-600 mt-1">{notif.message}</div>
                          <div className="text-xs text-stone-500 mt-2">
                            {new Date(notif.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Unavailability Tab */}
          <TabsContent value="unavailability">
            <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-stone-800">My Unavailability</h2>
                  <p className="text-sm text-stone-600 mt-1">Manage your unavailable dates</p>
                </div>
                <Button
                  onClick={() => setShowUnavailability(true)}
                  data-testid="add-unavailability-button"
                  className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Unavailability
                </Button>
              </div>

              {unavailability.length > 0 ? (
                <div className="space-y-3">
                  {unavailability.map(unavail => (
                    <div
                      key={unavail.id}
                      data-testid={`unavail-${unavail.id}`}
                      className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-sm text-stone-600">
                            {new Date(unavail.date_from).toLocaleDateString()} - {new Date(unavail.date_to).toLocaleDateString()}
                          </div>
                          {unavail.reason && (
                            <div className="text-xs text-stone-500 mt-1">{unavail.reason}</div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleDeleteUnavailability(unavail.id)}
                          data-testid={`delete-unavail-${unavail.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-stone-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-stone-400" />
                  <p>No unavailability periods set</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="attendance">
            <div className="bg-white rounded-xl shadow-md border border-stone-200 p-4 sm:p-6">
              <div className="mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-stone-800 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> My Attendance
                </h2>
                <p className="text-xs sm:text-sm text-stone-600 mt-1">Review your biometric clock-ins from TimeTec, sign off correct entries, or request edits.</p>
              </div>
              <AttendanceView mode="staff" />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Unavailability Dialog */}
      <Dialog open={showUnavailability} onOpenChange={setShowUnavailability}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Unavailability Period</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleAddUnavailability({
              date_from: formData.get('date_from'),
              date_to: formData.get('date_to'),
              reason: formData.get('reason')
            });
          }} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  name="date_from"
                  data-testid="unavail-date-from"
                  required
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  name="date_to"
                  data-testid="unavail-date-to"
                  required
                />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                name="reason"
                placeholder="e.g., Vacation, Personal"
                data-testid="unavail-reason"
                required
              />
            </div>
            <Button type="submit" className="w-full" data-testid="submit-unavailability">
              Add Unavailability
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
